import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useUser } from './UserContext';
import { useApiCaller } from '../utils/ApiUtilities';
import { getNotificationUnreadCountUrl } from '../utils/UrlUtilities';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../utils/Constants';

// Owns the unread-count badge state + the near-real-time polling loop. Delivery is
// abstracted here so a future SSE/WebSocket source could replace the poll without
// touching NavBar / MyNotifications.
//
// Real-time strategy (sync WSGI backend, no push infra): poll a cheap unread-count
// endpoint on an interval, but ONLY while the tab is visible, and re-poll instantly
// on refocus. Consumers call refreshUnread() right after reading so the badge updates
// immediately rather than waiting for the next tick.
const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
    const { userState } = useUser();
    const CallApi = useApiCaller();
    const [unreadCount, setUnreadCount] = useState(0);

    const isLoggedIn = Boolean(userState?.isLoggedIn);

    const refreshUnread = useCallback(async () => {
        if (!isLoggedIn) return;
        const res = await CallApi({ url: getNotificationUnreadCountUrl(), method: 'GET', token: true });
        if (res.success) setUnreadCount(res.data?.unread_count || 0);
    }, [isLoggedIn, CallApi]);

    useEffect(() => {
        if (!isLoggedIn) {
            setUnreadCount(0);
            return undefined;
        }

        let intervalId = null;
        const start = () => {
            if (!intervalId) intervalId = setInterval(refreshUnread, NOTIFICATION_POLL_INTERVAL_MS);
        };
        const stop = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };
        const onVisibility = () => {
            if (document.hidden) {
                stop();
            } else {
                refreshUnread(); // instant catch-up on refocus
                start();
            }
        };

        refreshUnread(); // initial poll on login/mount
        if (!document.hidden) start();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [isLoggedIn, refreshUnread]);

    return (
        <NotificationContext.Provider value={{ unreadCount, refreshUnread, setUnreadCount }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
